<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { ArrowLeft, Loader2, Camera, Key, Bell, Shield } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
  import { Switch } from '@/components/ui/switch'
  import {
    UsersService,
    type CustomerUser,
    type User,
    type SuperUserUser,
    RoleEnum,
    FlagEnum
  } from '@/types/generated'
  import { ROUTES } from '@/router/routes'
  import { useTranslation } from '@/composables/useTranslation'
  import { useToast } from '@/components/ui/toast'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'

  const { t } = useTranslation({
    en: {
      backToTeam: 'Back to team',
      editEmployee: 'Edit Employee',
      employeeInfo: 'Employee Information',
      configureSettings: 'Update the details for this team member',
      email: 'Email',
      enterEmail: 'Enter email address',
      firstName: 'First Name',
      enterFirstName: 'Enter first name',
      lastName: 'Last Name',
      enterLastName: 'Enter last name',
      phone: 'Phone',
      enterPhone: 'Enter phone number',
      role: 'Role',
      selectRole: 'Select a role',
      saving: 'Saving...',
      save: 'Save Changes',
      errorSaving: 'Error saving employee',
      successSaving: 'Employee updated successfully',
      lawyer: 'Lawyer',
      admin: 'Admin',
      clerk: 'Clerk',
      phoneAiUser: 'Phone AI user',
      emailRequired: 'Email is required',
      firstNameRequired: 'First name is required',
      lastNameRequired: 'Last name is required',
      invalidEmail: 'Please enter a valid email address',
      updatePhoto: 'Click to update profile photo',
      basicInformation: 'Basic Information',
      employeeRole: 'Employee Role',
      password: 'Password',
      passwordManagement: 'Manage employee password',
      passwordSecurityNote: 'Make sure to use a strong password',
      changePassword: 'Change Password',
      newPassword: 'New Password',
      enterNewPassword: 'Enter new password',
      cancel: 'Cancel',
      employeeNotFound: 'Employee not found',
      loadError: 'Failed to load employee data',
      notifications: 'Notifications',
      notificationsDescription: 'Receive email notifications',
      superuser: 'Superuser',
      superuserDescription: 'Only for JUPUS employees',
      additionalSettings: 'Additional Settings'
    },
    de: {
      backToTeam: 'Zurück zum Team',
      editEmployee: 'Mitarbeiter bearbeiten',
      employeeInfo: 'Mitarbeiterinformationen',
      configureSettings: 'Aktualisieren Sie die Details für dieses Teammitglied',
      email: 'E-Mail',
      enterEmail: 'E-Mail-Adresse eingeben',
      firstName: 'Vorname',
      enterFirstName: 'Vorname eingeben',
      lastName: 'Nachname',
      enterLastName: 'Nachname eingeben',
      phone: 'Telefon',
      enterPhone: 'Telefonnummer eingeben',
      role: 'Rolle',
      selectRole: 'Rolle auswählen',
      saving: 'Wird gespeichert...',
      save: 'Änderungen speichern',
      errorSaving: 'Fehler beim Speichern des Mitarbeiters',
      successSaving: 'Mitarbeiter erfolgreich aktualisiert',
      lawyer: 'Anwalt',
      admin: 'Administrator',
      clerk: 'Sachbearbeiter',
      phoneAiUser: 'Telefon-KI-Nutzer',
      emailRequired: 'E-Mail ist erforderlich',
      firstNameRequired: 'Vorname ist erforderlich',
      lastNameRequired: 'Nachname ist erforderlich',
      invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
      updatePhoto: 'Klicken Sie, um das Profilfoto zu aktualisieren',
      basicInformation: 'Grundinformationen',
      employeeRole: 'Mitarbeiterrolle',
      password: 'Passwort',
      passwordManagement: 'Passwort verwalten',
      passwordSecurityNote: 'Stellen Sie sicher, dass Sie ein sicheres Passwort verwenden',
      changePassword: 'Passwort ändern',
      newPassword: 'Neues Passwort',
      enterNewPassword: 'Neues Passwort eingeben',
      cancel: 'Abbrechen',
      employeeNotFound: 'Mitarbeiter nicht gefunden',
      loadError: 'Fehler beim Laden der Mitarbeiterdaten',
      notifications: 'Benachrichtigungen',
      notificationsDescription: 'E-Mail-Benachrichtigungen erhalten',
      superuser: 'Superuser',
      superuserDescription: 'Nur für JUPUS Mitarbeitende',
      additionalSettings: 'Weitere Einstellungen'
    },
    fr: {
      backToTeam: "Retour à l'équipe",
      editEmployee: 'Modifier le collaborateur',
      employeeInfo: 'Informations du collaborateur',
      configureSettings: "Mettez à jour les informations de ce membre de l'équipe",
      email: 'E-mail',
      enterEmail: "Saisir l'adresse e-mail",
      firstName: 'Prénom',
      enterFirstName: 'Saisir le prénom',
      lastName: 'Nom',
      enterLastName: 'Saisir le nom',
      phone: 'Téléphone',
      enterPhone: 'Saisir le numéro de téléphone',
      role: 'Rôle',
      selectRole: 'Sélectionner un rôle',
      saving: 'Enregistrement en cours...',
      save: 'Enregistrer les modifications',
      errorSaving: "Erreur lors de l'enregistrement du collaborateur",
      successSaving: 'Collaborateur mis à jour avec succès',
      lawyer: 'Avocat',
      admin: 'Administrateur',
      clerk: 'Collaborateur',
      phoneAiUser: 'Utilisateur IA téléphonique',
      emailRequired: "L'e-mail est requis",
      firstNameRequired: 'Le prénom est requis',
      lastNameRequired: 'Le nom est requis',
      invalidEmail: 'Veuillez saisir une adresse e-mail valide',
      updatePhoto: 'Cliquez pour modifier la photo de profil',
      basicInformation: 'Informations de base',
      employeeRole: 'Rôle du collaborateur',
      password: 'Mot de passe',
      passwordManagement: 'Gérer le mot de passe',
      passwordSecurityNote: "Assurez-vous d'utiliser un mot de passe fort",
      changePassword: 'Changer le mot de passe',
      newPassword: 'Nouveau mot de passe',
      enterNewPassword: 'Saisir un nouveau mot de passe',
      cancel: 'Annuler',
      employeeNotFound: 'Collaborateur introuvable',
      loadError: 'Erreur lors du chargement des données du collaborateur',
      notifications: 'Notifications',
      notificationsDescription: 'Recevoir les notifications par e-mail',
      superuser: 'Superutilisateur',
      superuserDescription: 'Uniquement pour les employés JUPUS',
      additionalSettings: 'Paramètres supplémentaires'
    }
  })

  const router = useRouter()
  const route = useRoute()
  const { toast } = useToast()

  const currentUser = ref<CustomerUser | null>(null)
  const employee = ref<User | null>(null)
  const isLoading = ref(false)
  const isInitialLoading = ref(true)
  const showPasswordField = ref(false)
  const newPassword = ref('')

  const form = ref({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: RoleEnum.LAWYER,
    notifications: false,
    is_superuser: false
  })

  const errors = ref<Record<string, string>>({})

  const employeeUuid = computed(() => route.params.uuid as string)

  const phonebotOnlyRoleFlag = useFeatureFlag(FlagEnum.PHONEBOT_ONLY_ROLE)

  const roleOptions = computed(() => {
    const options = [
      { value: RoleEnum.LAWYER, title: t('lawyer') },
      { value: RoleEnum.ADMIN, title: t('admin') },
      { value: RoleEnum.CLERK, title: t('clerk') }
    ]
    if (phonebotOnlyRoleFlag.isEnabled.value) {
      options.push({ value: RoleEnum.PHONE_AI_USER, title: t('phoneAiUser') })
    }
    return options
  })

  onMounted(async () => {
    phonebotOnlyRoleFlag.checkFlag()

    try {
      // Fetch current user and employee data in parallel
      const [userResponse, employeeResponse] = await Promise.all([
        UsersService.usersRetrieve({ uuid: 'current' }),
        UsersService.usersRetrieve({ uuid: employeeUuid.value })
      ])

      currentUser.value = userResponse
      employee.value = employeeResponse

      // Populate form with employee data
      form.value = {
        email: employeeResponse.email || '',
        first_name: employeeResponse.first_name || '',
        last_name: employeeResponse.last_name || '',
        phone: employeeResponse.phone || '',
        role:
          (employeeResponse as any).role ||
          (employeeResponse.is_lawfirm_admin ? RoleEnum.ADMIN : RoleEnum.LAWYER),
        notifications: (employeeResponse as any).notifications || false,
        is_superuser: (employeeResponse as SuperUserUser).is_superuser || false
      }
    } catch (error) {
      console.error('Failed to fetch data:', error)
      toast({
        title: t('loadError'),
        variant: 'destructive'
      })
      router.push(ROUTES.APP.SETTINGS.LAWFIRM.TEAM)
    } finally {
      isInitialLoading.value = false
    }
  })

  const validateForm = (): boolean => {
    errors.value = {}

    if (!form.value.email.trim()) {
      errors.value.email = t('emailRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
      errors.value.email = t('invalidEmail')
    }

    if (!form.value.first_name.trim()) {
      errors.value.first_name = t('firstNameRequired')
    }

    if (!form.value.last_name.trim()) {
      errors.value.last_name = t('lastNameRequired')
    }

    return Object.keys(errors.value).length === 0
  }

  const goBack = () => {
    router.push(ROUTES.APP.SETTINGS.LAWFIRM.TEAM)
  }

  const handleAvatarUpload = async (event: Event) => {
    const input = event.target as HTMLInputElement
    if (!input.files?.length || !employee.value) return

    const file = input.files[0]
    try {
      const updatedEmployee = await UsersService.usersUpdateAvatarCreate({
        uuid: employee.value.uuid,
        formData: {
          avatar: file
        }
      })
      employee.value = updatedEmployee
      toast({
        title: t('successSaving'),
        description: 'Avatar updated'
      })
    } catch (error) {
      console.error('Upload error:', error)
      toast({
        title: t('errorSaving'),
        description: 'Failed to upload avatar',
        variant: 'destructive'
      })
    }
  }

  const saveEmployee = async () => {
    if (!validateForm() || !employee.value) {
      return
    }

    try {
      isLoading.value = true

      // Build the request body with standard fields
      const requestBody: Record<string, unknown> = {
        email: form.value.email.trim().toLowerCase(),
        first_name: form.value.first_name.trim(),
        last_name: form.value.last_name.trim(),
        phone: form.value.phone?.trim() || null,
        role: form.value.role,
        notifications: form.value.notifications
      }

      // Only include is_superuser if current user is a superuser
      if (isCurrentUserSuperuser.value) {
        requestBody.is_superuser = form.value.is_superuser
      }

      // Update user details (use PATCH to only update provided fields)
      await UsersService.usersPartialUpdate({
        uuid: employee.value.uuid,
        requestBody
      })

      // Update password if changed
      if (newPassword.value) {
        await UsersService.usersAdminPasswordUpdatePartialUpdate({
          uuid: employee.value.uuid,
          requestBody: {
            new_password: newPassword.value
          }
        })

        // If user changed their own password, session is invalidated
        // Force a full page reload to trigger clean auth redirect
        if (isEditingSelf.value) {
          toast({
            title: t('successSaving')
          })
          window.location.href = ROUTES.APP.SETTINGS.LAWFIRM.TEAM
          return
        }
      }

      toast({
        title: t('successSaving')
      })

      router.push(ROUTES.APP.SETTINGS.LAWFIRM.TEAM)
    } catch (error: any) {
      console.error('Failed to save employee:', error)

      let errorMessage = ''
      if (error?.body?.email) {
        errorMessage = error.body.email[0]
      } else if (error?.body?.error) {
        errorMessage = error.body.error
      } else if (error?.message) {
        errorMessage = error.message
      } else {
        errorMessage = 'An error occurred'
      }

      toast({
        title: t('errorSaving'),
        description: errorMessage,
        variant: 'destructive'
      })
    } finally {
      isLoading.value = false
    }
  }

  const isFormValid = computed(() => {
    return (
      form.value.email.trim() &&
      form.value.first_name.trim() &&
      form.value.last_name.trim() &&
      form.value.role
    )
  })

  const isEditingSelf = computed(() => {
    return currentUser.value?.uuid === employee.value?.uuid
  })

  const isCurrentUserSuperuser = computed(() => {
    return (currentUser.value as SuperUserUser)?.is_superuser === true
  })
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Loading state -->
    <div
      v-if="isInitialLoading"
      class="flex min-h-screen items-center justify-center"
    >
      <Loader2 class="h-8 w-8 animate-spin text-purple-600" />
    </div>

    <template v-else-if="employee">
      <!-- Header -->
      <div class="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6">
        <div class="flex items-center gap-4">
          <button
            class="flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-gray-900"
            @click="goBack"
          >
            <ArrowLeft class="h-4 w-4" />
            {{ t('backToTeam') }}
          </button>
        </div>
      </div>

      <!-- Main content -->
      <div class="p-6">
        <div class="mx-auto max-w-2xl">
          <div class="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
            <div class="space-y-8">
              <!-- Form Header -->
              <div>
                <h2 class="mb-2 text-lg font-medium text-gray-900">{{ t('employeeInfo') }}</h2>
                <p class="text-sm text-gray-600">{{ t('configureSettings') }}</p>
              </div>

              <!-- Avatar Section -->
              <div class="flex items-center space-x-4">
                <div class="relative">
                  <Avatar class="h-20 w-20">
                    <AvatarImage
                      v-if="employee?.avatar"
                      :src="employee.avatar"
                      :alt="employee.first_name"
                    />
                    <AvatarFallback class="bg-purple-100 text-xl text-purple-600">
                      {{ employee?.first_name?.[0] }}{{ employee?.last_name?.[0] }}
                    </AvatarFallback>
                  </Avatar>

                  <label
                    class="absolute inset-0 flex cursor-pointer items-center justify-center rounded-full bg-black/60 opacity-0
                      transition-opacity hover:opacity-100"
                  >
                    <Camera class="h-6 w-6 text-white" />
                    <input
                      type="file"
                      class="hidden"
                      accept="image/*"
                      @change="handleAvatarUpload"
                    />
                  </label>
                </div>
                <span class="text-sm text-gray-500">{{ t('updatePhoto') }}</span>
              </div>

              <!-- Basic Information Section -->
              <div class="rounded-lg border border-gray-200 p-4">
                <h3 class="mb-4 font-medium">{{ t('basicInformation') }}</h3>
                <div class="grid gap-4">
                  <!-- Email -->
                  <div>
                    <InputField
                      v-model="form.email"
                      name="email"
                      type="email"
                      :label="t('email')"
                      :placeholder="t('enterEmail')"
                      required
                    />
                    <div
                      v-if="errors.email"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ errors.email }}
                    </div>
                  </div>

                  <!-- First Name -->
                  <div>
                    <InputField
                      v-model="form.first_name"
                      name="first_name"
                      :label="t('firstName')"
                      :placeholder="t('enterFirstName')"
                      required
                    />
                    <div
                      v-if="errors.first_name"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ errors.first_name }}
                    </div>
                  </div>

                  <!-- Last Name -->
                  <div>
                    <InputField
                      v-model="form.last_name"
                      name="last_name"
                      :label="t('lastName')"
                      :placeholder="t('enterLastName')"
                      required
                    />
                    <div
                      v-if="errors.last_name"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ errors.last_name }}
                    </div>
                  </div>

                  <!-- Phone -->
                  <div>
                    <InputField
                      v-model="form.phone"
                      name="phone"
                      type="tel"
                      :label="t('phone')"
                      :placeholder="t('enterPhone')"
                    />
                  </div>

                  <!-- Role -->
                  <div>
                    <SelectField
                      v-model="form.role"
                      :label="t('employeeRole')"
                      name="role"
                      :options="roleOptions"
                      :placeholder="t('selectRole')"
                      required
                    />
                  </div>
                </div>
              </div>

              <!-- Additional Settings Section -->
              <div class="space-y-4 rounded-lg border border-gray-200 p-4">
                <h3 class="font-medium">{{ t('additionalSettings') }}</h3>

                <!-- Notifications Toggle -->
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <Bell class="h-5 w-5 text-gray-500" />
                    <div class="space-y-0.5">
                      <label
                        for="notifications"
                        class="text-sm font-medium"
                      >
                        {{ t('notifications') }}
                      </label>
                      <p class="text-sm text-gray-500">{{ t('notificationsDescription') }}</p>
                    </div>
                  </div>
                  <Switch
                    id="notifications"
                    :checked="form.notifications"
                    @update:checked="form.notifications = $event"
                  />
                </div>

                <!-- Superuser Toggle - Only visible to superusers -->
                <div
                  v-if="isCurrentUserSuperuser"
                  class="flex items-center justify-between border-t border-gray-100 pt-4"
                >
                  <div class="flex items-center gap-3">
                    <Shield class="h-5 w-5 text-amber-500" />
                    <div class="space-y-0.5">
                      <label
                        for="is_superuser"
                        class="text-sm font-medium"
                      >
                        {{ t('superuser') }}
                      </label>
                      <p class="text-sm text-gray-500">{{ t('superuserDescription') }}</p>
                    </div>
                  </div>
                  <Switch
                    id="is_superuser"
                    :checked="form.is_superuser"
                    @update:checked="form.is_superuser = $event"
                  />
                </div>
              </div>

              <!-- Password Management Section -->
              <div class="space-y-4 rounded-lg border border-gray-200 p-4">
                <div class="flex items-center justify-between">
                  <div class="space-y-1">
                    <h3 class="font-medium">{{ t('password') }}</h3>
                    <p class="text-sm text-gray-500">{{ t('passwordManagement') }}</p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    class="flex items-center gap-2"
                    @click="showPasswordField = !showPasswordField"
                  >
                    <Key class="h-4 w-4" />
                    {{ showPasswordField ? t('cancel') : t('changePassword') }}
                  </Button>
                </div>

                <!-- Password Input Field - Shows/Hides based on button click -->
                <div
                  v-if="showPasswordField"
                  class="border-t border-gray-100 pt-3"
                >
                  <InputField
                    v-model="newPassword"
                    type="password"
                    name="new_password"
                    :label="t('newPassword')"
                    :placeholder="t('enterNewPassword')"
                  />
                  <p class="mt-2 text-sm text-gray-500">
                    {{ t('passwordSecurityNote') }}
                  </p>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  @click="goBack"
                >
                  {{ t('cancel') }}
                </Button>
                <Button
                  :disabled="isLoading || !isFormValid"
                  class="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
                  @click="saveEmployee"
                >
                  <Loader2
                    v-if="isLoading"
                    class="h-4 w-4 animate-spin"
                  />
                  {{ isLoading ? t('saving') : t('save') }}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
